import { isHospitalManager } from 'util/unified'
import { isScratchAdminUser, userHasRoleAccess } from 'util/user'
import { selectContext } from '../selectors/auth'
import { useSelector } from 'react-redux'

type Flags = {
	[key: string]: boolean
}

interface UseFlags {
	canViewBankAccountInfo: boolean
	canViewMonthlyStatement: boolean
	canViewIntegrationToken: boolean
	canViewProviderAgreement: boolean
	canViewMarketingMaterials: boolean
	canViewUsers: boolean
	isIndependentClinic: (string) => boolean
	flags: Flags
}

export const useFlags = (
	currentPracticeObject: IPractice,
	userRolePractice: UserRolePractice | undefined,
	user: User
): UseFlags => {
	const context = useSelector(selectContext)

	const isIndependentClinic = (consolidatorId) => {
		return (
			consolidatorId === undefined ||
			consolidatorId === null ||
			consolidatorId === '0' ||
			consolidatorId === '1' ||
			consolidatorId == ''
		)
	}

	// TODO: move to consolidator.featureFlags once applied SSoT
	const consolidatorLendingFlags =
		currentPracticeObject?.consolidator?.lendingFlags ??
		currentPracticeObject?.consolidator?.financingFlags

	const canViewBankAccountInfo = (() => {
		const isScratchAdmin = isScratchAdminUser(user)
		const isAdmin = user?.isAdmin || false
		const isManager = isHospitalManager(context, currentPracticeObject)
		const isAchOffPractice = currentPracticeObject?.originationFlags?.achOff || false
		const isAchOffConsolidator = consolidatorLendingFlags?.achOff || false

		if (isScratchAdmin) return true
		if (isAchOffPractice || isAchOffConsolidator) return false
		if (isAdmin) return true

		if (isManager) {
			return isIndependentClinic(currentPracticeObject?.consolidatorId)
		}

		return false
	})()

	const canViewMonthlyStatement = (() => {
		const isHideMonthlyStatementPractice =
			currentPracticeObject?.originationFlags?.hideMonthlyStatement || false
		const isHideMonthlyStatementConsolidator =
			consolidatorLendingFlags?.hideMonthlyStatement || false

		return !(isHideMonthlyStatementPractice || isHideMonthlyStatementConsolidator)
	})()

	const canViewIntegrationToken = (() => {
		const isHideIntegrationTokenPractice =
			currentPracticeObject?.originationFlags?.hideToken || false
		const isHideIntegrationTokenConsolidator = consolidatorLendingFlags?.hideToken || false

		return !(isHideIntegrationTokenPractice || isHideIntegrationTokenConsolidator)
	})()

	const canViewProviderAgreement = (() => {
		const isHideProviderAgreementPractice =
			currentPracticeObject?.originationFlags?.hideProviderAgreement || false
		const isHideProviderAgreementConsolidator =
			consolidatorLendingFlags?.hideProviderAgreement || false

		return !(isHideProviderAgreementPractice || isHideProviderAgreementConsolidator)
	})()

	const canViewMarketingMaterials = (() => {
		const isHideMarketingMaterialsPractice =
			currentPracticeObject?.originationFlags?.hideMarketingNav || false
		const isHideMarketingMaterialsConsolidator = consolidatorLendingFlags?.hideMarketingNav || false

		return !(isHideMarketingMaterialsPractice || isHideMarketingMaterialsConsolidator)
	})()

	const canViewUsers = userHasRoleAccess(user, userRolePractice, ['hospitalManager'])

	const flags: Flags = (() => ({
		...consolidatorLendingFlags,
		...currentPracticeObject?.originationFlags,
		achOff: !canViewBankAccountInfo,
		hideMonthlyStatement: !canViewMonthlyStatement,
		hideToken: !canViewIntegrationToken,
		hideProviderAgreement: !canViewProviderAgreement,
		hideMarketingNav: !canViewMarketingMaterials
	}))()

	return {
		canViewBankAccountInfo,
		canViewMonthlyStatement,
		canViewIntegrationToken,
		canViewProviderAgreement,
		canViewMarketingMaterials,
		canViewUsers,
		isIndependentClinic,
		flags
	}
}
